import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 操作履歴 API 操作クラス
 */
export default class FunctionHistories {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GETS FunctionHistories
   */
  public async gets(
    userStore: LoginUser,
    queryParams?: {
      start: string;
      end: string;
    }
  ): Promise<Array<FunctionHistoriesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.FunctionHistories, formatParams);
    const response = await this.commonService.executeGet<Array<FunctionHistoriesGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      queryParams,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }
}

/**
 * お知らせ
 */
export interface FunctionHistoriesGetModel {
  /** 施設ID */
  facilityId: number;
  /** 履歴日時 */
  datetime: number;

  /** 機能種別 */
  functionType: FunctionHistoryFunctionType;
  /** 履歴の対象となる情報を特定するためのキー情報 */
  keys: FunctionHistoryKey;
  /** タイトル */
  title: string;
  /** 内容 */
  text: string;

  /** 履歴を残したユーザーの施設 ID */
  userFacilityId: number;
  /** ユーザーID */
  userId: string;
  /** ユーザー名 */
  userName: string;

  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

/**
 * ### 履歴機能種別
 */
export enum FunctionHistoryFunctionType {
  UNKNOWN = 0,
  RESIDENTS = 1,
  FAMILIES = 2,
  OTHER_USER_GROUPS = 3,
  OTHER_USER_GROUPS_MEMBER = 4,
  WAITERS = 5,
  SEND_MESSAGE_GROUPS = 6,
  EVENTS = 101
}

/**
 * ### 履歴キー
 */
export interface FunctionHistoryKey {
  /** 施設ID */
  facilityId: number;
}

/**
 * - 機能種別が以下の場合に使用するキー情報
 *   - 利用者
 *   - 待機者
 */
export interface FunctionHistoryResidentsKey extends FunctionHistoryKey {
  /** 利用者ID */
  id: number;
  /** 家族ID */
  familyId: number;
  /** システムで一意の利用者ID */
  idInSystem: number;
}

/**
 * - 機能種別が以下の場合に使用するキー情報
 *   - 家族
 */
export interface FunctionHistoryFamiliesKey extends FunctionHistoryKey {
  /** 利用者ID */
  id: number;
  /** 家族ID */
  familyId: number;
}

/**
 * - 機能種別が以下の場合に使用するキー情報
 *   - 施設関係者グループ
 *   - 施設関係者グループメンバー
 */
export interface FunctionHistoryOtherUserGroupsKey extends FunctionHistoryKey {
  /** 利用者グループID */
  id: number;
  /** メンバーID */
  memberId: number;
}

/**
 * - 機能種別が以下の場合に使用するキー情報
 *   - 宛先グループ
 */
export interface FunctionHistoryOtherUserGroupsKey extends FunctionHistoryKey {
  /** ID */
  id: number;
  /** 宛先グループ種別 */
  type: number;
}
